import { useContext, useEffect, useState } from "react";
import { useAuthContext } from "../../../context/AuthContext";
import { LanguageContext } from "../../../context/LanguageContext";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, CircularProgress, Grid } from "@mui/material";
import { ParagraphText, UBButton, UBInput } from "../../../Libraries";
import TabAutoComplete from "../../../Libraries/TabAutoComplete";
import { InnerWrapper, MainContainer, TitleAndActionBox } from "../../common";
import { CATEGORY_LIST, uuid } from "../../../constants";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import { Sync } from "../../../SyncService";
import { bulkUpdateBusiness } from "../../../database/helpers/business";
import { useSnackbar } from "../../../context/SnackbarContext";
import styles from "./styles.module.css";
import { EnterIcon } from "../../../assets/icons/icon";

const CATEGORY_OPTIONS = CATEGORY_LIST.map((item) => ({
  value: item.name,
  label: item.name,
}));

export function CreateAndEditBusiness() {
  const { business, user, refreshBusiness, setBusiness } = useAuthContext();
  const [I18n] = useContext(LanguageContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();

  const isEdit =
    location?.state?.isEdit || location.pathname?.toLowerCase() === "/profile/edit";

  const [name, setName] = useState(isEdit ? business?.name || "" : "");
  const [category, setCategory] = useState("");
  const [otherCategory, setOtherCategory] = useState("");
  const [landmark, setLandmark] = useState(
    isEdit ? business?.landmark || "" : "",
  );
  const [saveLoading, setSaveLoading] = useState(false);
  const [error, setError] = useState({});
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);

  const isDisabled =
    !name.trim() ||
    !category ||
    (category === "Other" && !otherCategory.trim()) ||
    saveLoading;

  useEffect(() => {
    if (isEdit && business?.category) {
      const isExist = CATEGORY_LIST.find(
        (item) => item.name.toLowerCase() === business.category.toLowerCase(),
      );
      setOtherCategory(!isExist ? business.category : "");
      setCategory(isExist ? business.category : "Other");
    }
  }, []);

  const validate = () => {
    const newError = {};
    if (!name.trim()) newError.name = I18n.business_name_cannot_be_empty;
    if (!category)
      newError.category = I18n.please_select_your_business_category;
    if (category === "Other" && !otherCategory.trim())
      newError.otherCategory = I18n.please_select_your_business_category;
    setError(newError);
    return Object.keys(newError).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const finalCategory = category === "Other" ? otherCategory : category;
    try {
      setSaveLoading(true);
      if (isEdit) {
        await business.updateBusiness({
          name,
          category: finalCategory,
          landmark,
        });
        triggerLogEvent(analytics, "setting_profile_edited");
        refreshBusiness();
        Sync(user.phone_number, navigate);
        showSnackbar({
          message: I18n.profile_updated,
          severity: "success",
        });
      } else {
        const id = uuid();
        localStorage.setItem("business_id", id);
        const newBusiness = await user.createBusiness(name, id, false, finalCategory);
        if (landmark.trim()) await newBusiness.updateBusiness({ name, category: finalCategory, landmark });
        await bulkUpdateBusiness(name);
        setBusiness(newBusiness);
        triggerLogEvent(analytics, "setting_business_created");
        Sync(user.phone_number, navigate);
        showSnackbar({
          message: I18n.business_updated_success,
          severity: "success",
        });
        navigate("/Accounts");
        return;
      }
      navigate(-1);
    } catch (e) {
      showSnackbar({ message: I18n.something_went_wrong, severity: "error" });
    } finally {
      setSaveLoading(false);
    }
  };

  const onChangeText = (key, text) => {
    if (key === I18n.business_name_label) {
      if (error?.name) setError({ ...error, name: "" });
      setName(text);
    } else if (key === I18n.other_category_label) {
      if (error?.otherCategory)
        setError({ ...error, category: "", otherCategory: "" });
      setOtherCategory(text);
    } else if (key === I18n.landmark) {
      if (error?.landmark) setError({ ...error, landmark: "" });
      setLandmark(text);
    }
  };

  let phoneNumber = user?.phone_number || localStorage.getItem("phone_number")

  return (
    <Grid
      container
      className={`${styles.root} ${styles.SaleExpenseFormWrapper} SaleExpenseForm`}
    >
      <Grid item md={12}>
        <TitleAndActionBox
          data={{
            title: isEdit ? I18n.edit_profile : I18n.create_business,
            isBack: true,
          }}
        />

        <Box className={styles.formBodyGrid}>
          <Box className={styles.formColumn}>
            <Box
              className={`${styles.formBody} expenseFormBody`}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !isCategoryOpen && !isDisabled) {
                  e.preventDefault();
                  handleSave();
                }
              }}
            >
              <UBInput
                inputId="business_name"
                label={I18n.business_name_label}
                name={I18n.business_name_label}
                placeholder={I18n.business_name_placeholder}
                value={name}
                onChange={onChangeText}
                required
                error={{ show: !!error.name, text: error.name }}
                maxLength={34}
              />

              <TabAutoComplete
                id="business_category"
                label={I18n.business_category_label}
                list={CATEGORY_OPTIONS}
                tabList={[]}
                placeholder={I18n.business_category_label}
                value={category}
                required
                handleSelect={(item) => {
                  setCategory(item?.value || "");
                  if (error?.category) setError({ ...error, category: "" });
                }}
                onOpenChange={setIsCategoryOpen}
              />
              {error.category && !category && (
                <p className="inputError businessPlaceholder">
                  {error.category}
                </p>
              )}

              {category === "Other" && (
                <UBInput
                  inputId="other_category"
                  label={I18n.other_category_label}
                  name={I18n.other_category_label}
                  placeholder={I18n.enter_other_category}
                  value={otherCategory}
                  onChange={onChangeText}
                  error={{
                    show: !!error.otherCategory,
                    text: error.otherCategory,
                  }}
                  maxLength={34}
                />
              )}

              <UBInput
                inputId="mobile_number"
                label={I18n.mobile_number}
                name={I18n.mobile_number}
                value={phoneNumber || ""}
                onChange={() => {}}
                baseClassname={styles.mobileInput}
                disabled
              />

              <UBInput
                inputId="landmark"
                label={I18n.landmark}
                name={I18n.landmark}
                placeholder={I18n.enter_landmark}
                value={landmark}
                onChange={onChangeText}
                error={{ show: !!error.landmark, text: error.landmark }}
                maxLength={100}
              />

              <UBButton
                id={isEdit ? "edit_business" : "add_business"}
                title={<ParagraphText>{I18n.save} </ParagraphText>}
                iconComponent={<EnterIcon />}
                iconPlacement="right"
                disabled={isDisabled}
                className={"buttonPrimary saveSaleButton"}
                onClick={handleSave}
              />
            </Box>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
